import { useEffect, useMemo, useRef, useState } from "react";
import { portalFetch } from "../api/client";
import type {
	CreatedPortalOrganizationInvitationDto,
	PortalOrganizationAdministrationDto,
	PortalOrganizationInferenceDto,
	PortalOrganizationInferenceKeyDto,
	PortalOrganizationInferenceTestResultDto,
	PortalOrganizationMemberAccessDto,
	PortalOrganizationSummaryDto,
} from "../model";
import { PortalSelect } from "./PortalSelect";
import { StatusBanner } from "./StatusBanner";

type AccessDraft = { allProjects: boolean; selectedProjectIds: string[] };
type InvitationDraft = { intendedRecipientLabel: string; allProjects: boolean; selectedProjectIds: string[] };
type InferenceProvider = NonNullable<PortalOrganizationInferenceKeyDto["provider"]>;
type InferenceAction = "" | "key" | "removeKey" | "test";

const INFERENCE_PROVIDERS: readonly InferenceProvider[] = ["anthropic", "openai", "openrouter"];
const INFERENCE_ALIASES = ["planner", "worker", "research", "portal"] as const;

function aliasLabel(alias: string) {
	return `${alias.charAt(0).toUpperCase()}${alias.slice(1)}`;
}

function inferencePath(organizationId: string) {
	return `/api/portal/v1/organizations/${encodeURIComponent(organizationId)}/inference`;
}

/** The owner read: key presence and provenance, the read-only profile, and who covers the billing. */
export function loadPortalOrganizationInference(organizationId: string) {
	return portalFetch<PortalOrganizationInferenceDto>(inferencePath(organizationId));
}

/** Sets the first key or rotates the stored one. The value leaves the browser once and never returns. */
export function savePortalOrganizationKey(
	organizationId: string,
	input: { provider: InferenceProvider; apiKey: string },
) {
	return portalFetch<PortalOrganizationInferenceDto>(`${inferencePath(organizationId)}/key`, {
		method: "PUT",
		body: JSON.stringify(input),
	});
}

/** Removes the key. The administrator-owned profile and billing flag survive. */
export function removePortalOrganizationKey(organizationId: string) {
	return portalFetch<PortalOrganizationInferenceDto>(`${inferencePath(organizationId)}/key`, { method: "DELETE" });
}

/** The owner's Test action: one minimal completion call per alias. Nothing is persisted. */
export function testPortalOrganizationInference(organizationId: string) {
	return portalFetch<PortalOrganizationInferenceTestResultDto>(`${inferencePath(organizationId)}/test`, {
		method: "POST",
	});
}

/**
 * The one write-only key submission rule: read the field, send it once, and clear the field only
 * when the server accepted it, so a refused key stays available to correct and an accepted key is
 * gone from the browser.
 */
export async function submitOrganizationKey(
	field: { value: string } | null,
	provider: InferenceProvider,
	save: (input: { provider: InferenceProvider; apiKey: string }) => Promise<boolean>,
) {
	const apiKey = field?.value.trim() ?? "";
	if (!apiKey) return false;
	const saved = await save({ provider, apiKey });
	if (saved && field) field.value = "";
	return saved;
}

/**
 * The owner's Inference panel. The key is write-only: it is typed into an uncontrolled field, sent
 * once, and cleared imperatively, so neither React state nor a rendered attribute ever carries the
 * plaintext. The profile and the billing flag are administrator-owned, so they render as text with
 * no control that could change them.
 */
export function OrganizationInferencePanel({
	inference,
	test,
	busyAction,
	error,
	feedback,
	onSaveKey,
	onRemoveKey,
	onTest,
}: {
	inference: PortalOrganizationInferenceDto;
	test?: PortalOrganizationInferenceTestResultDto;
	busyAction: InferenceAction;
	error?: string;
	feedback?: string;
	onSaveKey: (input: { provider: InferenceProvider; apiKey: string }) => Promise<boolean>;
	onRemoveKey: () => Promise<boolean>;
	onTest: () => Promise<boolean>;
}) {
	const keyInput = useRef<HTMLInputElement>(null);
	const [provider, setProvider] = useState<InferenceProvider>(
		inference.key.provider ?? inference.profile?.provider ?? "anthropic",
	);
	const [hasKeyDraft, setHasKeyDraft] = useState(false);
	const busy = busyAction !== "";
	const profile = inference.profile;

	async function submitKey() {
		if (busy) return;
		if (await submitOrganizationKey(keyInput.current, provider, onSaveKey)) setHasKeyDraft(false);
	}

	return (
		<section className="organization-panel organization-inference" aria-labelledby="organization-inference-heading">
			<p className="eyebrow">INFERENCE</p>
			<h2 id="organization-inference-heading">Organization key and models</h2>
			{error && (
				<p className="organization-inference-error" role="alert">
					{error}
				</p>
			)}
			{feedback && (
				<p className="organization-inference-feedback" role="status">
					{feedback}
				</p>
			)}
			<section className="organization-inference-section" aria-labelledby="organization-inference-key">
				<h3 id="organization-inference-key">Key</h3>
				<dl className="invitation-summary organization-inference-state">
					<div>
						<dt>Key</dt>
						<dd>{inference.key.present ? "Present" : "Absent"}</dd>
					</div>
					<div>
						<dt>Provider</dt>
						<dd>{inference.key.provider ?? "None"}</dd>
					</div>
					<div>
						<dt>Fingerprint</dt>
						<dd>{inference.key.fingerprint ?? "None"}</dd>
					</div>
					<div>
						<dt>Last changed</dt>
						<dd>
							{inference.key.lastChangedAt ? new Date(inference.key.lastChangedAt).toLocaleString() : "Never"}
						</dd>
					</div>
				</dl>
				<p className="organization-inference-note">
					A stored key is write-only. Substrate can never show it again, so replace a lost key by rotating it.
				</p>
				<form
					className="organization-inference-key-form"
					onSubmit={(event) => {
						event.preventDefault();
						void submitKey();
					}}
				>
					<PortalSelect
						id="organization-key-provider"
						className="organization-inference-provider"
						label="Key provider"
						value={provider}
						disabled={busy}
						options={INFERENCE_PROVIDERS.map((item) => ({ value: item, label: item }))}
						onChange={(value) => setProvider(value as InferenceProvider)}
					/>
					<label htmlFor="organization-key-input">API key</label>
					<input
						id="organization-key-input"
						type="password"
						autoComplete="off"
						ref={keyInput}
						disabled={busy}
						placeholder={inference.key.present ? "Paste a replacement key" : "Paste the provider API key"}
						onChange={(event) => setHasKeyDraft(event.target.value.trim().length > 0)}
					/>
					<div className="form-actions">
						{inference.key.present && (
							<button type="button" className="secondary" disabled={busy} onClick={() => void onRemoveKey()}>
								{busyAction === "removeKey" ? "Removing…" : "Remove key"}
							</button>
						)}
						<button type="submit" disabled={busy || !hasKeyDraft}>
							{busyAction === "key" ? "Saving…" : inference.key.present ? "Rotate key" : "Set key"}
						</button>
					</div>
				</form>
			</section>
			<section className="organization-inference-section" aria-labelledby="organization-inference-profile">
				<h3 id="organization-inference-profile">Inference profile</h3>
				{profile ? (
					<>
						<dl className="invitation-summary organization-inference-state">
							<div>
								<dt>Harness</dt>
								<dd>{profile.harness}</dd>
							</div>
							<div>
								<dt>Provider</dt>
								<dd>{profile.provider}</dd>
							</div>
						</dl>
						<ul className="organization-inference-aliases">
							{INFERENCE_ALIASES.map((alias) => (
								<li key={alias}>
									<strong>{aliasLabel(alias)}</strong>
									<span>{profile.aliases[alias].model}</span>
									<span className="organization-inference-reasoning">
										{profile.aliases[alias].reasoningLevel}
									</span>
								</li>
							))}
						</ul>
					</>
				) : (
					<p>No inference profile has been saved for this organization yet.</p>
				)}
				<p className="organization-inference-note">
					A Substrate administrator owns the harness, provider, and models. Ask them to change this profile.
				</p>
				{inference.billingCoveredByWorkspace && (
					<p className="organization-inference-billing">billing covered by workspace</p>
				)}
			</section>
			<section className="organization-inference-section" aria-labelledby="organization-inference-test">
				<h3 id="organization-inference-test">Test</h3>
				<p>Every alias makes one minimal completion call on this key. Nothing is saved and nothing changes.</p>
				<div className="form-actions">
					<button type="button" disabled={busy} onClick={() => void onTest()}>
						{busyAction === "test" ? "Testing…" : "Test"}
					</button>
				</div>
				{test && (
					<ul className="organization-inference-test-results" aria-live="polite">
						{test.results.map((result) => (
							<li className={`organization-inference-test-row status-${result.status}`} key={result.alias}>
								<strong>{aliasLabel(result.alias)}</strong>
								<span className={`organization-inference-test-status status-${result.status}`}>
									{result.status === "pass" ? "Pass" : "Fail"}
								</span>
								{result.error && <p className="organization-inference-test-error">{result.error}</p>}
							</li>
						))}
					</ul>
				)}
			</section>
		</section>
	);
}

export function OrganizationView({ organizationId }: { organizationId?: string }) {
	const [organizations, setOrganizations] = useState<PortalOrganizationSummaryDto[]>([]);
	const [selectedOrganizationId, setSelectedOrganizationId] = useState(organizationId);
	const [organization, setOrganization] = useState<PortalOrganizationAdministrationDto>();
	const [selectedMembershipId, setSelectedMembershipId] = useState<string>();
	const [access, setAccess] = useState<PortalOrganizationMemberAccessDto>();
	const [draft, setDraft] = useState<AccessDraft>();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [status, setStatus] = useState<string>();
	const [renaming, setRenaming] = useState(false);
	const [renameDraft, setRenameDraft] = useState("");
	const [inviting, setInviting] = useState(false);
	const [invitationDraft, setInvitationDraft] = useState<InvitationDraft>({
		intendedRecipientLabel: "",
		allProjects: false,
		selectedProjectIds: [],
	});
	const [invitationUrl, setInvitationUrl] = useState<string>();
	const [inference, setInference] = useState<PortalOrganizationInferenceDto>();
	const [inferenceTest, setInferenceTest] = useState<PortalOrganizationInferenceTestResultDto>();
	const [inferenceAction, setInferenceAction] = useState<InferenceAction>("");
	const [inferenceError, setInferenceError] = useState("");
	const [inferenceFeedback, setInferenceFeedback] = useState("");

	useEffect(() => {
		let active = true;
		portalFetch<PortalOrganizationSummaryDto[]>("/api/portal/v1/organizations")
			.then((items) => {
				if (!active) return;
				setOrganizations(items);
				setSelectedOrganizationId((current) => current ?? items.find((item) => item.role === "administrator")?.id);
			})
			.catch(() => setStatus("Organizations could not be loaded."))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (!selectedOrganizationId) {
			setOrganization(undefined);
			return;
		}
		setInviting(false);
		setInvitationUrl(undefined);
		setInvitationDraft({ intendedRecipientLabel: "", allProjects: false, selectedProjectIds: [] });
		const summary = organizations.find((item) => item.id === selectedOrganizationId);
		if (summary?.role !== "administrator") {
			setOrganization(undefined);
			return;
		}
		let active = true;
		portalFetch<PortalOrganizationAdministrationDto>(
			`/api/portal/v1/organizations/${encodeURIComponent(selectedOrganizationId)}`,
		)
			.then((item) => {
				if (!active) return;
				setOrganization(item);
				setSelectedMembershipId(item.members.find((member) => member.role === "member")?.membershipId);
			})
			.catch(() => active && setStatus("Organization administration could not be loaded."));
		return () => {
			active = false;
		};
	}, [organizations, selectedOrganizationId]);

	useEffect(() => {
		if (!selectedOrganizationId || !selectedMembershipId) {
			setAccess(undefined);
			setDraft(undefined);
			return;
		}
		let active = true;
		portalFetch<PortalOrganizationMemberAccessDto>(
			`/api/portal/v1/organizations/${encodeURIComponent(selectedOrganizationId)}/members/${encodeURIComponent(selectedMembershipId)}/access`,
		)
			.then((item) => {
				if (!active) return;
				setAccess(item);
				setDraft({ allProjects: item.allProjects, selectedProjectIds: item.selectedProjectIds });
			})
			.catch(() => active && setStatus("Member access could not be loaded."));
		return () => {
			active = false;
		};
	}, [selectedOrganizationId, selectedMembershipId]);

	// The organization's own inference settings. Only an administrator of the organization can read them.
	useEffect(() => {
		setInference(undefined);
		setInferenceTest(undefined);
		setInferenceError("");
		setInferenceFeedback("");
		if (!selectedOrganizationId) return;
		if (organizations.find((item) => item.id === selectedOrganizationId)?.role !== "administrator") return;
		let active = true;
		loadPortalOrganizationInference(selectedOrganizationId)
			.then((item) => active && setInference(item))
			.catch(() => active && setInferenceError("Inference settings could not be loaded."));
		return () => {
			active = false;
		};
	}, [organizations, selectedOrganizationId]);

	const selectedMember = useMemo(
		() => organization?.members.find((member) => member.membershipId === selectedMembershipId),
		[organization, selectedMembershipId],
	);
	const configurableMembers = useMemo(
		() => organization?.members.filter((member) => member.role === "member") ?? [],
		[organization],
	);

	function toggleProject(projectId: string) {
		setDraft((current) => {
			if (!current) return current;
			return {
				...current,
				selectedProjectIds: current.selectedProjectIds.includes(projectId)
					? current.selectedProjectIds.filter((id) => id !== projectId)
					: [...current.selectedProjectIds, projectId],
			};
		});
	}

	function toggleInvitationProject(projectId: string) {
		setInvitationDraft((current) => ({
			...current,
			selectedProjectIds: current.selectedProjectIds.includes(projectId)
				? current.selectedProjectIds.filter((id) => id !== projectId)
				: [...current.selectedProjectIds, projectId],
		}));
	}

	async function saveName() {
		if (!selectedOrganizationId || !organization || !renameDraft.trim() || saving) return;
		setSaving(true);
		setStatus(undefined);
		try {
			const updated = await portalFetch<{ id: string; name: string; status: "active" }>(
				`/api/portal/v1/organizations/${encodeURIComponent(selectedOrganizationId)}`,
				{ method: "PATCH", body: JSON.stringify({ name: renameDraft.trim() }) },
			);
			setOrganization({ ...organization, name: updated.name });
			setOrganizations((current) =>
				current.map((item) => (item.id === updated.id ? { ...item, name: updated.name } : item)),
			);
			setRenaming(false);
			setStatus("Organization renamed.");
		} catch {
			setStatus("Organization name could not be changed. Nothing changed.");
		} finally {
			setSaving(false);
		}
	}

	async function saveAccess() {
		if (!selectedOrganizationId || !selectedMembershipId || !draft || saving) return;
		setSaving(true);
		setStatus(undefined);
		try {
			const updated = await portalFetch<PortalOrganizationMemberAccessDto>(
				`/api/portal/v1/organizations/${encodeURIComponent(selectedOrganizationId)}/members/${encodeURIComponent(selectedMembershipId)}/access`,
				{ method: "PATCH", body: JSON.stringify(draft) },
			);
			setAccess(updated);
			setDraft({ allProjects: updated.allProjects, selectedProjectIds: updated.selectedProjectIds });
			setStatus(`Access updated for ${selectedMember?.displayName ?? "member"}.`);
		} catch {
			setStatus("Access could not be updated. Nothing changed.");
		} finally {
			setSaving(false);
		}
	}

	async function createInvitation() {
		if (!selectedOrganizationId || !invitationDraft.intendedRecipientLabel.trim() || saving) return;
		setSaving(true);
		setStatus(undefined);
		try {
			const created = await portalFetch<CreatedPortalOrganizationInvitationDto>(
				`/api/portal/v1/organizations/${encodeURIComponent(selectedOrganizationId)}/invitations`,
				{
					method: "POST",
					body: JSON.stringify({
						intendedRecipientLabel: invitationDraft.intendedRecipientLabel.trim(),
						allProjects: invitationDraft.allProjects,
						selectedProjectIds: invitationDraft.allProjects ? [] : invitationDraft.selectedProjectIds,
					}),
				},
			);
			setInvitationUrl(created.invitationUrl);
			setStatus("Invitation link created.");
		} catch {
			setStatus("The invitation link could not be created. Nothing changed.");
		} finally {
			setSaving(false);
		}
	}

	/** Every inference change is the server's answer: the returned settings replace what is shown. */
	async function changeInference(
		action: InferenceAction,
		work: (organizationId: string) => Promise<PortalOrganizationInferenceDto>,
		message: string,
	) {
		if (!selectedOrganizationId || inferenceAction) return false;
		setInferenceAction(action);
		setInferenceError("");
		setInferenceFeedback("");
		try {
			setInference(await work(selectedOrganizationId));
			setInferenceFeedback(message);
			return true;
		} catch (error) {
			setInferenceError(error instanceof Error ? error.message : "The change could not be saved.");
			return false;
		} finally {
			setInferenceAction("");
		}
	}

	async function runInferenceTest() {
		if (!selectedOrganizationId || inferenceAction) return false;
		setInferenceAction("test");
		setInferenceError("");
		setInferenceFeedback("");
		try {
			setInferenceTest(await testPortalOrganizationInference(selectedOrganizationId));
			return true;
		} catch (error) {
			setInferenceError(error instanceof Error ? error.message : "The test could not be run.");
			return false;
		} finally {
			setInferenceAction("");
		}
	}

	async function copyInvitationLink() {
		if (!invitationUrl) return;
		try {
			await navigator.clipboard.writeText(invitationUrl);
			setStatus("Invitation link copied.");
		} catch {
			setStatus("Copy failed. Select the invitation link and copy it manually.");
		}
	}

	return (
		<>
			<header className="route-heading">
				<p className="eyebrow">ORGANIZATION ACCESS</p>
				<h1 tabIndex={-1}>Organizations</h1>
				<p className="route-deck">
					See your organization role and configure member access without changing identity.
				</p>
			</header>
			{loading ? (
				<p aria-live="polite">Loading organizations…</p>
			) : organizations.length === 0 ? (
				<section className="organization-empty">
					<h2>No organizations yet</h2>
					<p>Your direct projects remain available from Projects.</p>
				</section>
			) : (
				<div className="organization-layout">
					<nav className="organization-list" aria-label="Your organizations">
						{organizations.map((item) => (
							<button
								type="button"
								key={item.id}
								className={item.id === selectedOrganizationId ? "active" : undefined}
								onClick={() => setSelectedOrganizationId(item.id)}
							>
								<strong>{item.name}</strong>
								<span>{item.role === "administrator" ? "Administrator" : "Member"}</span>
							</button>
						))}
					</nav>
					{organizations.find((item) => item.id === selectedOrganizationId)?.role === "member" ? (
						<section className="organization-panel">
							<h2>Member access</h2>
							<p>
								Open Projects to see the organization projects granted to you. Organization filtering changes
								presentation only.
							</p>
						</section>
					) : organization ? (
						<section className="organization-panel" aria-labelledby="organization-name">
							<div className="organization-panel-heading">
								<div>
									<p className="eyebrow">ADMINISTRATION</p>
									<h2 id="organization-name">{organization.name}</h2>
								</div>
								<div className="organization-heading-actions">
									<span>{configurableMembers.length} Members</span>
									<button
										type="button"
										className="secondary"
										onClick={() => {
											setInvitationUrl(undefined);
											setInviting(true);
										}}
									>
										Invite member
									</button>
									<button
										type="button"
										className="secondary"
										onClick={() => {
											setRenameDraft(organization.name);
											setRenaming(true);
										}}
									>
										Rename
									</button>
								</div>
							</div>
							{renaming && (
								<form
									className="organization-rename"
									onSubmit={(event) => {
										event.preventDefault();
										void saveName();
									}}
								>
									<label htmlFor="organization-name-input">Organization name</label>
									<input
										id="organization-name-input"
										value={renameDraft}
										maxLength={255}
										required
										onChange={(event) => setRenameDraft(event.target.value)}
									/>
									<div>
										<button
											type="button"
											className="secondary"
											disabled={saving}
											onClick={() => setRenaming(false)}
										>
											Cancel
										</button>
										<button type="submit" disabled={saving || !renameDraft.trim()}>
											{saving ? "Saving…" : "Save name"}
										</button>
									</div>
								</form>
							)}
							{inviting && (
								<form
									className="organization-invite"
									aria-label="Invite a member"
									onSubmit={(event) => {
										event.preventDefault();
										void createInvitation();
									}}
								>
									<h3>Invite a member</h3>
									{invitationUrl ? (
										<div className="organization-invitation-result">
											<p>
												<strong>Share this link yourself.</strong> It is shown only now and expires after 14
												days.
											</p>
											<label htmlFor="organization-invitation-link">Invitation link</label>
											<input id="organization-invitation-link" value={invitationUrl} readOnly />
											<div className="form-actions">
												<button type="button" className="secondary" onClick={() => setInviting(false)}>
													Done
												</button>
												<button type="button" onClick={() => void copyInvitationLink()}>
													Copy invitation link
												</button>
											</div>
										</div>
									) : (
										<>
											<label htmlFor="organization-invite-label">Invitee name or label</label>
											<input
												id="organization-invite-label"
												value={invitationDraft.intendedRecipientLabel}
												maxLength={255}
												required
												onChange={(event) =>
													setInvitationDraft((current) => ({
														...current,
														intendedRecipientLabel: event.target.value,
													}))
												}
											/>
											<fieldset>
												<legend>Initial project access</legend>
												<label className="organization-policy">
													<input
														type="checkbox"
														checked={invitationDraft.allProjects}
														onChange={(event) =>
															setInvitationDraft((current) => ({
																...current,
																allProjects: event.target.checked,
																selectedProjectIds: event.target.checked
																	? []
																	: current.selectedProjectIds,
															}))
														}
													/>
													<span>
														<strong>All current and future organization projects</strong>
														<small>
															Off by default. You can change this after the invitation is accepted.
														</small>
													</span>
												</label>
												<div className="organization-projects" aria-disabled={invitationDraft.allProjects}>
													{organization.availableProjects.map((project) => (
														<label key={project.id}>
															<input
																type="checkbox"
																disabled={invitationDraft.allProjects}
																checked={invitationDraft.selectedProjectIds.includes(project.id)}
																onChange={() => toggleInvitationProject(project.id)}
															/>
															<span>{project.name}</span>
														</label>
													))}
													{organization.availableProjects.length === 0 && (
														<p>
															No organization-owned projects are available yet. A global administrator
															must assign projects to this organization in Command Center before they can
															be included in an invitation.
														</p>
													)}
												</div>
											</fieldset>
											<div className="form-actions">
												<button
													type="button"
													className="secondary"
													disabled={saving}
													onClick={() => setInviting(false)}
												>
													Cancel
												</button>
												<button
													type="submit"
													disabled={saving || !invitationDraft.intendedRecipientLabel.trim()}
												>
													{saving ? "Creating…" : "Create invitation link"}
												</button>
											</div>
										</>
									)}
								</form>
							)}
							{configurableMembers.length === 0 ? (
								<div className="organization-member-empty">
									<strong>No members yet</strong>
									<p>Invite someone to add the first Member to this organization.</p>
								</div>
							) : (
								<PortalSelect
									id="organization-member"
									className="organization-member-select"
									label="Configure member"
									value={selectedMembershipId ?? ""}
									options={configurableMembers.map((member) => ({
										value: member.membershipId,
										label: member.displayName,
									}))}
									onChange={(membershipId) => setSelectedMembershipId(membershipId || undefined)}
								/>
							)}
							<p className="organization-admin-note">
								Administrators inherently access every current and future organization project. Members receive
								only the access configured below.
							</p>
							{selectedMember && draft && access && (
								<form
									className="member-access-form"
									onSubmit={(event) => {
										event.preventDefault();
										void saveAccess();
									}}
								>
									<fieldset>
										<legend>Project access for {selectedMember.displayName}</legend>
										<label className="organization-policy">
											<input
												type="checkbox"
												checked={draft.allProjects}
												onChange={(event) =>
													setDraft({
														allProjects: event.target.checked,
														selectedProjectIds: event.target.checked ? [] : draft.selectedProjectIds,
													})
												}
											/>
											<span>
												<strong>All current and future organization projects</strong>
												<small>
													Off by default. When enabled, this Member receives every project this
													organization owns now and later.
												</small>
											</span>
										</label>
										<div className="organization-projects" aria-disabled={draft.allProjects}>
											{access.availableProjects.map((project) => (
												<label key={project.id}>
													<input
														type="checkbox"
														disabled={draft.allProjects}
														checked={draft.allProjects || draft.selectedProjectIds.includes(project.id)}
														onChange={() => toggleProject(project.id)}
													/>
													<span>{project.name}</span>
												</label>
											))}
											{access.availableProjects.length === 0 && (
												<p>
													No organization-owned projects are available. A global administrator must assign
													projects to this organization in Command Center before member access can be
													granted.
												</p>
											)}
										</div>
									</fieldset>
									<div className="form-actions">
										<button
											type="button"
											className="text-button"
											disabled={saving}
											onClick={() =>
												setDraft({
													allProjects: access.allProjects,
													selectedProjectIds: access.selectedProjectIds,
												})
											}
										>
											Cancel
										</button>
										<button type="submit" disabled={saving}>
											{saving ? "Saving…" : "Save member access"}
										</button>
									</div>
								</form>
							)}
						</section>
					) : null}
					{inference && (
						<OrganizationInferencePanel
							inference={inference}
							test={inferenceTest}
							busyAction={inferenceAction}
							error={inferenceError}
							feedback={inferenceFeedback}
							onSaveKey={(input) =>
								changeInference(
									"key",
									(organizationId) => savePortalOrganizationKey(organizationId, input),
									inference.key.present ? "Organization key rotated." : "Organization key saved.",
								)
							}
							onRemoveKey={async () => {
								if (!window.confirm("Remove this organization key? A removed key cannot be recovered."))
									return false;
								return changeInference("removeKey", removePortalOrganizationKey, "Organization key removed.");
							}}
							onTest={runInferenceTest}
						/>
					)}
				</div>
			)}
			{status && (
				<StatusBanner
					kind={
						status.startsWith("Access updated") ||
						status === "Organization renamed." ||
						status === "Invitation link created." ||
						status === "Invitation link copied."
							? "success"
							: "error"
					}
				>
					{status}
				</StatusBanner>
			)}
		</>
	);
}
